import { all, put, takeEvery, takeLatest, select, call } from "redux-saga/effects";
import { SearchActions, NotificationsActions } from "@/sagas/actions";
import { fetchDetails, fetchDigitalObjects, fetchSearch, fetchDigitalObjectSourcePaths, fetchRelationsByGeo } from "@/api/search";
import { NotificationVariants } from "./notificationHelpers";
import { toast } from "sonner";
import savedSearch from "@/hooks/savedSearchClass";
const selectKeyword = (state: any) => state.search.query.query;
const selectCategoriesMeta = (state: any) => state.settings.categories;
const selectSearchingRelated = (state: any) => state.search.searchingRelated;




const saveSearch = () => {
    const s = new savedSearch();
    s.setSavedSearch()
}

const relationObjectContainsQuery = (relation: any, queryLower: string): boolean => {
    if (!relation || !queryLower) return false;
    return Object.values(relation).some((value) =>
        value !== null &&
        value !== undefined &&
        String(value).toLowerCase().includes(queryLower),
    );
};

const filterRelationsByQuery = (response: any, rawQuery: string) => {
    const queryLower = (rawQuery ?? '').trim().toLowerCase();
    if (!queryLower) return response;

    const responseItems = Array.isArray(response?.items) ? response.items : [];
    const filteredItems = responseItems
        .map((item: any) => {
            const parents = Array.isArray(item?.parents) ? item.parents : [];
            const children = Array.isArray(item?.children) ? item.children : [];
            const matchedParents = parents.filter((parent: any) => relationObjectContainsQuery(parent, queryLower));
            const matchedChildren = children.filter((child: any) => relationObjectContainsQuery(child, queryLower));
            if (matchedParents.length === 0 && matchedChildren.length === 0) return null;
            return {
                ...item,
                parents: matchedParents,
                children: matchedChildren,
            };
        })
        .filter((item: any) => item !== null);

    return {
        ...response,
        total: filteredItems.length,
        items: filteredItems,
    };
};

const flattenRelationItemsForMap = (response: any) => {
    const responseItems = Array.isArray(response?.items) ? response.items : [];
    const flattened = responseItems.flatMap((item: any) => {
        const parents = Array.isArray(item?.parents) ? item.parents : [];
        const children = Array.isArray(item?.children) ? item.children : [];
        return [...parents, ...children].filter(Boolean);
    });

    if (flattened.length > 0) {
        return {
            ...response,
            total: flattened.length,
            items: flattened,
        };
    }

    // Fallback for APIs that already return relation records directly in items.
    return response;
};
const workerInitialSearch = function* (): Generator<any> {

    try {
        yield put({ type: SearchActions.SEARCH_STARTED })
        yield put({ type: SearchActions.SET_URL, payload: '/results' });
        const keyword = yield select(selectKeyword);
        const filters = yield select((state: any) => state.search.query.filters);
        const geoFilter = yield select((state: any) => state.search.geoFilter);
        const sort_by = yield select((state: any) => state.search.query.sort_by);
        const sort_order = yield select((state: any) => state.search.query.sort_order);
        const pageSize = yield select((state: any) => state.search.query.pageSize);
        const pageN = 0;
        const searchingRelated = yield select(selectSearchingRelated);
        const selectedItem = yield select((state: any) => state.search.selectedItem);
        const response = yield fetchSearch(keyword, filters, pageN, searchingRelated ? selectedItem?.identifier : undefined, geoFilter, sort_by, sort_order, pageSize);
        saveSearch();
        yield put({ type: SearchActions.INITIAL_SEARCH_SUCCESS, payload: response });
    } catch (error) {
        toast.error('Error fetching search results');
        yield put({ type: SearchActions.SEARCH_ERROR, payload: error });
    }
}

const workerRelatedSearch = function* (filtered?: boolean): Generator<any> {
    try {
        yield put({ type: SearchActions.SEARCH_STARTED })
        const filters = yield select((state: any) => state.search.query.filters);
        const pageN = yield select((state: any) => state.search.query.page);
        const searchingRelated = yield select(selectSearchingRelated);
        const withinQuery = yield select(selectKeyword);
        const response = yield fetchSearch(withinQuery ? withinQuery : '', filters, pageN, searchingRelated);
        if (filtered && withinQuery?.length > 0 && withinQuery !== '**') {
            //todo: test if it shouldn't do RELATED_SEARCH_FILTERED_SUCCESS instead (search for 6.b.bu and refine for 'pelagia')
            yield put({ type: SearchActions.RELATED_SEARCH_SUCCESS, payload: { identifier: searchingRelated, result: response } });
        } else {
            yield put({ type: SearchActions.RELATED_SEARCH_SUCCESS, payload: { identifier: searchingRelated, result: response } });
        }
    } catch (error: any) {
        const errorInfo = { title: 'Failed fetching related search', description: error.message, variant: NotificationVariants.ERROR }
        yield put({ type: NotificationsActions.ADD_NOTIFICATION, payload: errorInfo });
        yield put({ type: SearchActions.SEARCH_ERROR, payload: error });
    }
}

const workerAppendingSearch = function* (): Generator<any> {
    const searchingRelated = yield select(selectSearchingRelated);
    if (searchingRelated) {
        console.error('Appending search with searchingRelated is not supported');
        yield put({ type: SearchActions.APPENDING_SEARCH_SUCCESS })
        return;
    } else {
        try {
            yield put({ type: SearchActions.SEARCH_STARTED })
            const keyword = yield select(selectKeyword);
            const filters = yield select((state: any) => state.search.query.filters);
            const geoFilter = yield select((state: any) => state.search.geoFilter);
            const sort_by = yield select((state: any) => state.search.query.sort_by);
            const sort_order = yield select((state: any) => state.search.query.sort_order);
            const pageSize = yield select((state: any) => state.search.query.pageSize);
            const pageN = yield select((state: any) => state.search.query.page);
            const response = yield fetchSearch(keyword, filters, pageN, searchingRelated, geoFilter, sort_by, sort_order, pageSize);
            yield put({ type: SearchActions.APPENDING_SEARCH_SUCCESS, payload: { ...response, _fetchedPage: pageN } });
            saveSearch();
        } catch (error) {
            yield put({ type: SearchActions.SEARCH_ERROR, payload: error });
            yield put({ type: NotificationsActions.ADD_NOTIFICATION, payload: { title: 'Failed', description: 'Failed to fetch more data', variant: NotificationVariants.ERROR } });
        }
    }
}

const workerFilteredSearch = function* (): Generator<any> {
    try {
        yield put({ type: SearchActions.SEARCH_STARTED })
        yield put({ type: SearchActions.SET_PAGE, payload: 0 });
        const keyword = yield select(selectKeyword);
        const filters = yield select((state: any) => state.search.query.filters);
        const geoFilter = yield select((state: any) => state.search.geoFilter);
        const sort_by = yield select((state: any) => state.search.query.sort_by);
        const sort_order = yield select((state: any) => state.search.query.sort_order);
        const pageSize = yield select((state: any) => state.search.query.pageSize);
        const page = yield select((state: any) => state.search.query.page);
        const searchingRelated = yield select(selectSearchingRelated);
        const response = yield fetchSearch(keyword, filters, page, searchingRelated, geoFilter, sort_by, sort_order, pageSize);
        saveSearch();
        yield put({ type: SearchActions.FILTERED_SEARCH_SUCCESS, payload: response });
    } catch (error) {
        yield put({ type: SearchActions.SEARCH_ERROR, payload: error });
        yield put({ type: NotificationsActions.ADD_NOTIFICATION, payload: { type: 'error', message: 'Error fetching search results' } });
    }
}

const workerFetchDetails = function* (action: any): Generator<any> {
    try {
        const response = yield fetchDetails(action.payload.identifier)
        yield put({ type: SearchActions.FETCH_DETAILS_SUCCESS, payload: response })
    } catch (error) {
        yield put({ type: SearchActions.FETCH_DETAILS_ERROR, payload: error })
    }
}

const workerFetchParentDetails = function* (action: any): Generator<any> {
    try {
        const response = yield fetchDetails(action.payload.identifier)
        yield put({
            type: SearchActions.FETCH_PARENT_DETAILS_SUCCESS, payload: {
                ...response,
                requestIdentifier: action.payload.identifier
            }
        })
    } catch (error: any) {
        console.log('Error fetching parent details:', error);

        // Check if it's a 404 error
        const is404 = error.message && (
            error.message.includes('404') ||
            error.message.includes('HTTP error! status: 404')
        );

        yield put({
            type: SearchActions.FETCH_PARENT_DETAILS_ERROR,
            payload: {
                error,
                requestIdentifier: action.payload.identifier,
                is404,
                message: is404 ? 'Parent not accessible' : error.message
            }
        })
    }
}

const workerUpdateCategorySelection = function* (action: any): Generator<any> {
    const categories = yield select(selectCategoriesMeta);
    const selection = action.payload;
    const selectedCategories = categories.filter((category: any) => selection.includes(category.indexName));
    yield put({ type: SearchActions.UPDATE_CATEGORY_TAGS, payload: selectedCategories });
}

const workerClearState = function* (): Generator<any> {
    yield localStorage.removeItem('persist:root');
    yield localStorage.removeItem('persist:search');
    const s = new savedSearch();
    s.clearSavedSearch();
    window.location.href = '/';
}

const workerFetchDos = function* (action: any): Generator<any> {
    try {
        // Pass the digitalObjectName and sourceName filters to the API
        const response = yield fetchDigitalObjects(
            action.payload.identifier,
            action.payload.sortColumn,
            action.payload.sortDirection,
            action.payload.pageNumber || 0,
            action.payload.pageSize || 25,
            action.payload.digitalObjectName || "",
            action.payload.sourceName || ""
        );
        yield put({ type: SearchActions.FETCH_DOS_SUCCESS, payload: response });
    } catch (error: any) {
        console.error('fetchDigitalObjects error:', error);
        yield put({ type: SearchActions.FETCH_DOS_ERROR, payload: error });
        yield put({ type: NotificationsActions.ADD_NOTIFICATION, payload: { title: 'Failed fetching digital objects', description: error.message, variant: NotificationVariants.ERROR } });
    }
};

const workerFetchDigitalObjectSourcePaths = function* (action: any): Generator<any> {
    try {
        const { identifier } = action.payload;
        const response = yield call(fetchDigitalObjectSourcePaths, identifier);

        yield put({
            type: SearchActions.FETCH_DIGITAL_OBJECT_SOURCE_PATHS_SUCCESS,
            payload: { identifier, data: response }
        });
    } catch (error: any) {
        const { identifier } = action.payload;
        console.error('fetchDigitalObjectSourcePaths error:', error);
        yield put({
            type: SearchActions.FETCH_DIGITAL_OBJECT_SOURCE_PATHS_ERROR,
            payload: { identifier, error: error.message }
        });
        yield put({
            type: NotificationsActions.ADD_NOTIFICATION,
            payload: {
                title: 'Failed fetching digital object source paths',
                description: error.message,
                variant: NotificationVariants.ERROR
            }
        });
    }
};


const workerFilterMapByRelations = function* (action: any): Generator<any> {
    const { query } = action.payload ?? {};
    if (!query?.trim()) {
        yield put({ type: SearchActions.CLEAR_RELATIONS_FILTER });
        return;
    }
    const items: any[] = (yield select((state: any) => state.search.items)) ?? [];
    const filtered = filterRelationsByQuery({ items, total: items.length }, query);
    yield put({ type: SearchActions.CHILDREN_GEO_SEARCH_SUCCESS, payload: filtered });
};

const workerChildrenByGeoSearch = function* (action: any): Generator<any> {
    try {
        yield put({ type: SearchActions.SEARCH_STARTED });
        yield put({ type: SearchActions.SET_PAGE, payload: 0 });
        const geoFilter = action.payload?.geoFilter ?? (yield select((state: any) => state.search.geoFilter));
        if (!geoFilter) return;
        const { query = '', dateFilter = null } = action.payload ?? {};
        const baseFilters = yield select((state: any) => state.search.query.filters);
        const dateMin = dateFilter?.min?.trim?.() ?? '';
        const dateMax = dateFilter?.max?.trim?.() ?? '';
        const mergedFilters =
            dateMin || dateMax
                ? [...baseFilters, { field: 'created', values: [dateMin, dateMax], type: 'range' }]
                : baseFilters;
        const pageSize = yield select((state: any) => state.search.query.pageSize);
        const sort_by = yield select((state: any) => state.search.query.sort_by);
        const sort_order = yield select((state: any) => state.search.query.sort_order);
        const response = yield call(fetchRelationsByGeo, geoFilter, query, mergedFilters, 0, pageSize, sort_by, sort_order);
        const filteredResponse = filterRelationsByQuery(response, query);
        const normalizedResponse = flattenRelationItemsForMap(filteredResponse);
        yield put({ type: SearchActions.CHILDREN_GEO_SEARCH_SUCCESS, payload: normalizedResponse });
    } catch (error: any) {
        toast.error('Error fetching relations by geo');
        yield put({ type: SearchActions.CHILDREN_GEO_SEARCH_ERROR, payload: error });
    }
};

export function* watcherSearchActions() {
    yield all([
        takeLatest(SearchActions.INITIAL_SEARCH, workerInitialSearch),
        takeEvery(SearchActions.APPENDING_SEARCH, workerAppendingSearch),
        takeEvery(SearchActions.RELATED_SEARCH, workerRelatedSearch),
        takeEvery(SearchActions.RELATED_SEARCH_FILTERED, () => workerRelatedSearch(true)),
        takeLatest(SearchActions.FILTERED_SEARCH, workerFilteredSearch),
        takeEvery(SearchActions.FETCH_DETAILS, workerFetchDetails),
        takeEvery(SearchActions.FETCH_PARENT_DETAILS, workerFetchParentDetails),
        takeEvery(SearchActions.UPDATE_CATEGORY_SELECTION, workerUpdateCategorySelection),
        takeEvery(SearchActions.CLEAR_STATE, workerClearState),
        takeEvery(SearchActions.FETCH_DOS, workerFetchDos), // Assuming this is also a related search
        takeEvery(SearchActions.FETCH_DIGITAL_OBJECT_SOURCE_PATHS, workerFetchDigitalObjectSourcePaths),
        takeLatest(SearchActions.CHILDREN_GEO_SEARCH, workerChildrenByGeoSearch),
        takeLatest(SearchActions.FILTER_MAP_BY_RELATIONS, workerFilterMapByRelations),
    ])
}